import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import Input from "../../../../components/elements/form/input";
import Teaxtarea from "../../../../components/elements/form/textarea";
import Datepicker from "../../../../components/elements/form/datepicker";
import Button from "../../../../components/elements/button";
import { useAppLoader } from "../../../../_common/pageLoader/appLoaderHook";
import toast from "react-hot-toast";
import FileUpload from "../../../../components/elements/form/fileUplaod";
import { useDepreciationApi } from "../../../../store/api/hooks/depreciationApiHook";
import { useParams } from "react-router-dom";
import { useAssetsApi } from "../../../../store/api/hooks/assetsApiHook";
import Label from "../../../../components/elements/form/fromLabel";
import moment from "moment";
import Currency from "../../../../components/currency";

interface RevaluateFormProps {
	_ref?: any;
	editData?: any;
	purchaseDate?: any;
	usefulLife?: any;
	onCreate?: () => void;
	onCancel?: () => void;
}

type AssetsFormValueType = {
	assetID?: number;
	revaluationID: string;
	revaluedAmount: string;
	lifeSpan: string;
	effectiveDate: Date | null;
	extendedLifeSpan: string;
	revaluationImage: any;
	notes: string;
};

const CreateRevaluate: React.FC<RevaluateFormProps> = ({ _ref, editData, purchaseDate, usefulLife, onCreate = () => { }, onCancel = () => { } }) => {
	const { control, handleSubmit, setValue, reset, formState: { errors }, watch } = useForm<AssetsFormValueType>({ defaultValues: {} });
	const { showLoader, hideLoader } = useAppLoader();
	const [assetCode, setassetCode] = useState<any>(null);
	const [actualBookValue, setActualBookValue] = useState<any>(null);
	const [rowDetails, setRowdetails] = useState<any>(null);
	const [Lifespan, setLifespan] = useState<any>("0.0");
	const [journalEntries, setJournalEntries] = useState<any[]>([]);
	const [forceNextYearEffectiveDate, setForceNextYearEffectiveDate] = useState(false);
	const [revaluationData, setRevaluationData] = useState<any>(null);

	const params: any = useParams();
	const depreciationAPI = useDepreciationApi();
	const assetsAPI = useAssetsApi();

	// Memoized function to format lifespan as "X years Y months"
	const formatLifespan = useCallback((lifespan: string | number): string => {
		const lifespanNum = parseFloat(lifespan.toString());
		if (isNaN(lifespanNum) || lifespanNum <= 0) return "0 years";
		
		const years = Math.floor(lifespanNum);
		const months = Math.round((lifespanNum - years) * 12);
		
		if (months === 0) {
			return `${years} year${years !== 1 ? 's' : ''}`;
		} else if (years === 0) {
			return `${months} month${months !== 1 ? 's' : ''}`;
		} else {
			return `${years} year${years !== 1 ? 's' : ''} ${months} month${months !== 1 ? 's' : ''}`;
		}
	}, []);

	// Memoized formatted lifespan to prevent re-calculation on every render
	const formattedLifespan = useMemo(() => formatLifespan(Lifespan), [Lifespan, formatLifespan]);

	/* const onSubmit: SubmitHandler<AssetsFormValueType> = (data) => {
		let payload: any = {
			assetID: Number(params.id),
			effectiveDate: data.effectiveDate,
			revaluedAmount: Number(data.revaluedAmount),
			actualBookValue: Number(actualBookValue),
			extendedLifeSpan: Number(data.extendedLifeSpan),
			notes: data.notes,
		};

		if (editData) payload.assetID = editData.assetID;

		showLoader();
		depreciationAPI.SaveRevaluation(payload, (resp: any) => {
			console.log();
			
			const formData = new FormData();
			if (data.revaluationImage instanceof File) {
				formData.append("inputFile", data.revaluationImage);
				formData.append("revaluationID", editData ? editData.assetID : resp?.returnID);
				depreciationAPI.SaveRevaluationAttachment(formData, () => {
					toast.success(resp?.message);
					onCreate();
					formReset();
					hideLoader();
				}, (error: any) => {
					hideLoader();
					toast.error(error);
				});
			}
			onCreate();
			onCancel();
			toast.success(resp?.message);
			formReset();
			hideLoader();
		}, (error: any) => {
			hideLoader();
			toast.error(error);
		});
	}; */

	
	const onSubmit: SubmitHandler<AssetsFormValueType> = (data) => {
		// Format the effective date to ensure consistent behavior across all conditions
		const formattedEffectiveDate = data.effectiveDate 
			? new Date(data.effectiveDate.getTime() - (data.effectiveDate.getTimezoneOffset() * 60000))
			: null;

		const payload: any = {
			assetID: Number(params.id),
			effectiveDate: formattedEffectiveDate,
			revaluedAmount: Number(data.revaluedAmount),
			actualBookValue: Number(actualBookValue),
			extendedLifeSpan: Number(data.extendedLifeSpan),
			notes: data.notes,
			costBasis: revaluationData && revaluationData.length > 0 ? revaluationData[0].costBasis : rowDetails?.purchaseCost || 0,
		};

		if (editData) {
			payload.assetID = editData.assetID;
		}

		showLoader();

		depreciationAPI.SaveRevaluation(payload, (resp: any) => {
			// If there is an image file, upload it first
			if (data.revaluationImage instanceof File) {
				const formData = new FormData();
				formData.append("inputFile", data.revaluationImage);
				formData.append("revaluationID", editData ? editData.assetID : resp?.returnID);

				depreciationAPI.SaveRevaluationAttachment(
					formData,
					() => {
						toast.success(resp?.message);
						onCreate();
						formReset();
						onCancel();
						hideLoader();
					},
					(error: any) => {
						hideLoader();
						toast.error(error);
					}
				);
			} else {
				// No image to upload, finish the process
				toast.success(resp?.message);
				onCreate();
				formReset();
				onCancel();
				hideLoader();
			}
		}, (error: any) => {
			hideLoader();
			toast.error(error);
		});
	};

	const GetAssetBookValue = (EffectDate: any) => {
		const payload = { AssetID: params.id, RequestDate: EffectDate };
		depreciationAPI.GetAssetBookValue(payload, (resp: any) => {
			if (rowDetails?.purchaseDate && rowDetails?.usefulLife) {
				const endDate = new Date(rowDetails.purchaseDate);
				endDate.setFullYear(endDate.getFullYear() + rowDetails.usefulLife);
				const diffInYears = Math.max(0,((endDate.getTime() - EffectDate.getTime()) / (1000 * 3600 * 24 * 30.44 * 12))).toFixed(1);
				setLifespan(diffInYears);
			} else {
				setLifespan("0.0");
			}
			setActualBookValue(resp?.bookValue?.toFixed(2) || "0.00");
		}, (error: any) => toast.error(error));
	};

	const GetJournalEntryByAssetId = () => {
		const payload = { assetID: params.id, reasonFor: 'Asset Revaluation' };
		assetsAPI.GetJournalEntryByAssetId(payload, (resp: any) => setJournalEntries(resp?.journalEntryResponseList || []), (error: any) => toast.error(error));
	};

	const formReset = () => {
		let effectiveDate: Date;
		
		// Check if there's revaluation data and find the latest effective date
		if (revaluationData && revaluationData.length > 0) {
			const latestRevaluation = revaluationData.reduce((latest: any, current: any) => {
				const latestDate = new Date(latest.effectiveDate);
				const currentDate = new Date(current.effectiveDate);
				return currentDate > latestDate ? current : latest;
			});
			
			// Set effective date to the day after the latest revaluation effective date
			effectiveDate = new Date(latestRevaluation.effectiveDate);
			effectiveDate.setDate(effectiveDate.getDate() + 1);
		} else if (rowDetails?.purchaseDate) {
			// If no revaluation data, use the day after purchase date as default
			effectiveDate = new Date(rowDetails.purchaseDate);
			effectiveDate.setDate(effectiveDate.getDate() + 1);
		} else if (forceNextYearEffectiveDate) {
			effectiveDate = new Date();
			effectiveDate.setFullYear(effectiveDate.getFullYear() + 1);
			effectiveDate.setMonth(0);
			effectiveDate.setDate(1);
		} else {
			effectiveDate = new Date();
			effectiveDate.setDate(effectiveDate.getDate() + 1); // Tomorrow
		}

		reset({
			effectiveDate,
			revaluedAmount: '',
			extendedLifeSpan: '',
			notes: '',
			revaluationImage: ''
		});

		setActualBookValue('0.00');
		setLifespan('0.0');
		GetAssetBookValue(effectiveDate);
	};

	const GetGAAPDepreciation = (assetID: any = params.id) => {
		const payload = { assetID };
		depreciationAPI.GetGAAPDepreciation(payload, (data: any) => {
			const currentYear = new Date().getFullYear();
			const shouldStartFromNextYear = data.depreciationList?.some((item: any) => {
				const itemYear = moment(item.depreciationDate).isValid()
					? moment(item.depreciationDate).year()
					: null;
				return item.isPushToSage === 1 && itemYear === currentYear;
			});
			setForceNextYearEffectiveDate(shouldStartFromNextYear);
			
			if (shouldStartFromNextYear && (!revaluationData || revaluationData.length === 0)) {
				const nextYearDate = new Date();
				nextYearDate.setFullYear(currentYear + 1, 0, 1);
				setValue("effectiveDate", nextYearDate);
				GetAssetBookValue(nextYearDate);
			}
		}, (error: any) => console.log("error =>", error));
	};

	const getAssetsList = (assetID: any) => {
		assetsAPI.getAssetsList({ assetID }, (data: any) => setRowdetails(data.assetList[0]), () => { });
	};

	const GetRevaluationList = (assetID: any = params.id) => {
		const payload = {
			assetID: assetID,
		};
		depreciationAPI.GetRevaluationList(
			payload,
			(data: any) => {
				setRevaluationData(data.revaluationList);
			},
			(error: any) => { }
		);
	};

	useEffect(() => {
		if (params.id) {
			GetGAAPDepreciation(params.id);
			getAssetsList(params.id);
			GetRevaluationList(params.id);
		}
	}, [params.id]);

	useEffect(() => {
		if (rowDetails && !editData) formReset();
	}, [rowDetails, editData, revaluationData]);

	useEffect(() => {
		if (_ref?.current) _ref.current.reset = formReset;
	}, [_ref]);

	useEffect(() => {
		GetJournalEntryByAssetId();
	}, []);



	return (

		<form onSubmit={handleSubmit(onSubmit)} ref={_ref} className="flex flex-col h-full">
			<div className="flex-1 overflow-auto">
				<h4 className="font-semibold text-[0.938rem] border-b border-gray-200 py-4 uppercase"> Basic Information </h4>
				{
					editData && editData?.assetID && <div className="bg-green-50 py-2 px-3">
						<h6 className="text-[0.813rem] font-semibold">Asset ID</h6>
						<p className="text-[0.813rem] font-normal mt-2">{editData?.assetCode}</p>
					</div>
				}

				<Controller
					name="effectiveDate"
					control={control}
					rules={{ required: "Effective Date Required" }}
					render={({ field: { value, onChange } }) => {
						// Find the minimum effective date based on revaluation data or purchase date
						const getMinEffectiveDate = () => {
							if (revaluationData && revaluationData.length > 0) {
								// If revaluation data exists, find the latest effective date
								const latestRevaluation = revaluationData.reduce((latest: any, current: any) => {
									const latestDate = new Date(latest.effectiveDate);
									const currentDate = new Date(current.effectiveDate);
									return currentDate > latestDate ? current : latest;
								});
								
								// Return the day after the latest effective date
								const nextDay = new Date(latestRevaluation.effectiveDate);
								nextDay.setDate(nextDay.getDate() + 1);
								return nextDay;
							} else if (rowDetails?.purchaseDate) {
								// If no revaluation data, use purchase date as minimum
								return new Date(rowDetails.purchaseDate);
							}
							
							// Fallback to current date logic if no data available
							if (forceNextYearEffectiveDate) {
								const nextYearDate = new Date();
								nextYearDate.setFullYear(nextYearDate.getFullYear() + 1);
								nextYearDate.setMonth(0);
								nextYearDate.setDate(1);
								return nextYearDate;
							} else {
								const today = new Date();
								today.setDate(1);
								return today;
							}
						};

						return (
							<Datepicker
								label="Effective Date"
								required
								value={value}
								onChange={(e) => {
									const minDate = getMinEffectiveDate();
									if (e && e instanceof Date && e <= minDate) {
										toast.error(`Effective Date must be after ${minDate.toLocaleDateString()}`);
										return;
									}
									onChange(e);
									if (e instanceof Date) {
										GetAssetBookValue(e);
									}
								}}
								minDate={getMinEffectiveDate()}
								error={errors.effectiveDate?.message}
							/>
						);
					}}
				/>

				<div className="bg-green-50 py-2 px-3">
					<div className="flex justify-between">
						<div>
							<h6 className="text-[0.813rem] font-semibold">Current Book Value</h6>
							<p className="text-[0.813rem] font-normal mt-2"><Currency currency="USD" value={actualBookValue || 0} /></p>
						</div>
						<div className="text-right">
							<h6 className="text-[0.813rem] font-semibold">Cost Basis</h6>
							<p className="text-[0.813rem] font-normal mt-2"><Currency currency="USD" value={revaluationData && revaluationData.length > 0 ? revaluationData[0].revaluedAmount : rowDetails?.purchaseCost || 0} /></p>
						</div>
					</div>
				</div>

				<Controller
					name="revaluedAmount"
					control={control}
					rules={{ required: "Revalue Amount Required" }}
					render={({ field: { value, onChange } }) => (
						<Input
							label="Revalue Amount"
							required
							type="number"
							value={value}
							onChange={(e) => {
								const inputValue = Number(e.target.value);
								const costBasis = revaluationData && revaluationData.length > 0 ? revaluationData[0].revaluedAmount : rowDetails?.purchaseCost || 0;
								if (inputValue >= costBasis) {
									toast.error("Revalue Amount cannot be greater than or equal to Cost Basis");
									onChange("");
								} else {
									onChange(e.target.value);
								}
							}}
							placeholder="Write Revalue Amount Here"
							error={errors.revaluedAmount?.message}
						/>
					)}
				/>
				<div className="bg-green-50 py-2 px-3">
					<h6 className="text-[0.813rem] font-semibold">Remaining Useful Life</h6>
					<p className="text-[0.813rem] font-normal mt-2">{formattedLifespan}</p>
				</div>
				<Controller
					name="extendedLifeSpan"
					control={control}
					rules={{ 
						required: "New Life Span Required",
						validate: (value) => {
							if (value && (value.includes('.') || value.includes(','))) {
								return "Please enter a whole number only";
							}
							return true;
						}
					}}
					render={({ field: { value, onChange } }) => (
						<Input
							label="New Life Span (Months)"
							required
							type="number"
							value={value}
							onChange={(e) => {
								const inputValue = e.target.value;
								// Only allow integers, block decimals
								if (inputValue === '' || /^\d+$/.test(inputValue)) {
									onChange(inputValue);
								}
							}}
							onKeyDown={(e) => {
								// Block decimal point and comma keys
								if (e.key === '.' || e.key === ',') {
									e.preventDefault();
								}
							}}
							placeholder="Enter Life Span in Months"
							error={errors.extendedLifeSpan?.message}
						/>
					)}
				/>

				<Controller
					name="notes"
					rules={{ required: "Notes is required" }}
					control={control}
					render={({ field: { value, onChange } }) => (
						<Teaxtarea
							label="Notes"
							value={value}
							onChange={onChange}
							required
							placeholder="Write Your Notes Here"
							error={errors.notes?.message}
						/>
					)}
				/>

				<Controller
					name='revaluationImage'
					control={control}
					render={({ field: { value, onChange } }) => (
						<FileUpload
							label='Upload Files'
							value={value}
							accept=".jpg, .jpeg, .png"
							onChange={(e: any) => {
								onChange(e.target.files[0])
							}}
							placeholder='Choose Processing Facility'

						/>
					)}
				/>
				{/* <div className={`mb-8`}>
					<div className="flex justify-between  mb-2 my-6 mx-4 pb-4  ml-0">
						<Label text={"JOURNAL ENTRIES"} isBold={true} />

					</div>
					<div className="max-w-[673px] rounded border border-[#043E6133] overflow-hidden">
						
						<div className="flex items-center bg-[#f0f0f0] h-[52px]">
							<div className="px-2 py-4 font-medium text-left text-[13px] text-[#262A2D] w-[calc(100%-220px)]">
								Account
							</div>
							<div className="px-2 py-4 font-medium text-right text-[13px] text-[#262A2D] w-[110px]">
								Debit ($)
							</div>
							<div className="px-2 py-4 font-medium text-right text-[13px] text-[#262A2D] w-[110px]">
								Credit ($)
							</div>
						</div>

						
						{journalEntries.length > 0 ? (
							journalEntries.map((row: any, index: number) => (
								<div key={index} className="flex items-center border-t border-[#043E6133]">
									<div className="px-2 py-2 text-left font-light text-[13px] text-[#262A2D] w-[calc(100%-220px)]">
										{row.accountDescription}
									</div>
									<div className="px-2 py-2 text-right font-light text-[13px] text-[#262A2D] w-[110px]">
										{row.type === 'debit' ? row.amount.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '------'}
									</div>
									<div className="px-2 py-2 text-right font-light text-[13px] text-[#262A2D] w-[110px]">
										{row.type === 'credit' ? row.amount.toLocaleString('en-US', { minimumFractionDigits: 2 }) : '------'}
									</div>
								</div>
							))
						) : (
							<div className="flex items-center justify-center border-t border-[#043E6133] py-4 text-[13px] text-gray-500">
								No journal entries found.
							</div>
						)}

					</div>

				</div>
				<hr className="border-gray-900" /> */}
			</div>
			<div className="flex justify-end gap-2 flex-shrink-0 pt-2 border-t border-gray-200">
				<Button type="button" theme="secondary" onClick={() => {
					formReset();
					onCancel()
				}}>
					Cancel
				</Button>
				<Button type="submit" working={false}>
					Save
				</Button>
			</div>
		</form>
	);
};

export default CreateRevaluate;
